import Img from '@/components/common/Img';
import { ComponentProps, ISize } from '@/types/cms';
import { FC, Fragment, useEffect, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { useInView } from 'react-intersection-observer';
import { useComponentTheme } from '../../hooks/useComponentTheme';
import { CroppedImage } from '@weee-fe/module';

type IProps = {
  // 设置背景是否在可视区
  setBgInView: (inView: boolean) => void;
  // 可视区计算的rootMargin
  inViewRootMargin?: string;
  // 页面主题配置
  pageTheme: Record<string, any> | null;
} & ComponentProps;

const TitleRich: FC<IProps> = ({ setBgInView, inViewRootMargin, pageTheme = null, ...props }) => {
  const { properties } = props;
  const { title, desc, sub_title, style = 'v2' } = properties;

  const { titleStyle, background: bgInfo, subTitleStyle } = useComponentTheme({ properties, pageTheme });

  const titleWithLineBreaks = useMemo(() => {
    if (!title) return '';
    return title?.split('\n').map((line, index) => {
      return (
        <Fragment key={index}>
          {line}
          <br />
        </Fragment>
      );
    });
  }, [title]);

  const { ref: inViewRef, inView } = useInView({
    initialInView: true,
    rootMargin: inViewRootMargin || '0px'
  });

  useEffect(() => {
    setBgInView(inView);
  }, [inView, setBgInView]);

  if (style === 'v2') {
    return (
      <div
        data-module={props.cmsComponentKey}
        ref={inViewRef}
        className="relative overflow-hidden"
        style={{ minHeight: '180px' }}>
        {bgInfo.type === 'image' && (
          <CroppedImage
            className="block box-border w-full object-cover z-0"
            url={bgInfo.value ?? ''}
            configs={{ renderWidth: 390 }}
            lazyLoad={false}
            alt=""
            aria-hidden="true"
          />
        )}
        {/* title */}
        {!!title && (
          <h2
            className={twMerge('absolute bottom-5 left-5', 'font-medium tracking-[-2%]')}
            style={{
              color: `${titleStyle?.color || pageTheme?.title_color}`,
              textAlign: titleStyle?.align,
              fontSize: '33px',
              lineHeight: '33px',
              width: '200px',
              wordBreak: 'break-word',
              minHeight: '66px',
              margin: 0
            }}>
            {titleWithLineBreaks}
          </h2>
        )}
      </div>
    );
  }

  return (
    <div data-module={props.cmsComponentKey}>
      <div className="relative mb-400 overflow-hidden">
        <Img
          className="absolute top-0 left-0 right-0 w-full object-cover z-0"
          croppedImageOptions={{ renderWidth: 750 }}
          src={bgInfo.value ?? ''}
          alt=""
          aria-hidden="true"
        />
        <div className="relative z-[1] pt-[210px] px-5">
          {/* subtitle */}
          {!!sub_title && (
            <p
              className={twMerge(
                'enki-body-2xs',
                'mb-[5px]',
                subTitleStyle?.font_size === (ISize.small as string) ? 'text-sm' : '',
                subTitleStyle?.font_size === (ISize.middle as string) ? 'text-base' : '',
                subTitleStyle?.font_size === (ISize.large as string) ? 'text-xl' : ''
              )}
              style={{ color: `${subTitleStyle?.color}`, textAlign: subTitleStyle?.align }}>
              {sub_title}
            </p>
          )}

          {/* title */}
          <h2
            ref={inViewRef}
            className={twMerge(
              'font-medium tracking-[-0.896px]',
              titleStyle?.font_size === (ISize.small as string) ? 'text-sm' : '',
              titleStyle?.font_size === (ISize.middle as string) ? 'text-base' : '',
              titleStyle?.font_size === (ISize.large as string) ? 'text-xl' : ''
            )}
            style={{
              color: `${titleStyle?.color || pageTheme?.title_color}`,
              textAlign: titleStyle?.align,
              // TODO: bakery 这里的字体大小和lineHeight 如何定义？
              fontSize: '45px',
              lineHeight: '45px',
              margin: 0
            }}>
            {title}
          </h2>
          {/* desc */}
          {!!desc && <p className={twMerge('enki-body-base-medium text-surface-100-fg-minor mt-2.5')}>{desc}</p>}
        </div>
      </div>
    </div>
  );
};

export default TitleRich;
